import { Injectable } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { noSpacesValidator } from '@core/validators/no-spaces.validator'
import { CreateWarrantyInput, UpdateWarrantyInput, Warranty, WarrantyTemplate } from '@generated/sammathnaur.graphql'
import { addDays, format, parseISO, set } from 'date-fns'
import { WarrantyForm } from '../warranties.type'

@Injectable({ providedIn: 'root' })
export class WarrantiesMapperService {
  public createForm(this: void, warranty: Warranty): FormGroup<WarrantyForm> {
    const extendedCoverageDurationDaysValidators = warranty.extendedCoverageDurationDays ? [Validators.required, Validators.min(1), Validators.max(2147483647)] : []
    const extendedValueValidators = warranty.extendedCoverageDurationDays ? [Validators.min(1), Validators.max(99999999.99)] : []
    const maintenancesTypes = warranty.maintenancesTypes?.map((type) => type.name) ?? null

    return new FormGroup({
      id: new FormControl<string>(warranty.id!),
      name: new FormControl<string | null>(warranty.name ?? null),
      description: new FormControl<string | null>(warranty.description ?? null, [Validators.maxLength(2000), Validators.required, noSpacesValidator()]),
      value: new FormControl<number | null>(warranty.value ?? null, [Validators.required, Validators.min(0), Validators.max(99999999.99)]),
      coverageDurationDays: new FormControl<number | null>(warranty.coverageDurationDays ?? null, [Validators.required, Validators.min(1), Validators.max(2147483647)]),
      extendedCoverageDurationDays: new FormControl<number | null>(warranty.extendedCoverageDurationDays ?? null, extendedCoverageDurationDaysValidators),
      extendedValue: new FormControl<number | null>(warranty.extendedValue ?? null, extendedValueValidators),
      coverageStartedAt: new FormControl<string | null>(warranty.startDate ?? null),
      warrantyTemplateId: new FormControl<string | null>(warranty.template?.id ?? null, [Validators.required]),
      maintenancesTypes: new FormControl<string[] | null>(maintenancesTypes),
      companyId: new FormControl<string | null>(warranty.company?.id ?? null),

      hasCoverageExtend: new FormControl<boolean>(Boolean(warranty.extendedCoverageDurationDays)),

      coverageEndedAt: new FormControl<string | null>(warranty.endDate ?? null),
      extendedCoverageEndedAt: new FormControl<string | null>(warranty.extendedEndDate ?? null),
    })
  }

  public warrantyTemplateCreateWarrantyForm(this: void, warrantyTemplate: WarrantyTemplate, coverageStartedAt: string, companyId: string | null | undefined): FormGroup<WarrantyForm> {
    const extendedCoverageDurationDaysValidators = warrantyTemplate.extendedCoverageDurationDays ? [Validators.min(1), Validators.max(2147483647)] : []
    const extendedValueValidators = warrantyTemplate.extendedCoverageDurationDays ? [Validators.min(1), Validators.max(99999999.99)] : []
    const coverageStartedAtDate = parseISO(coverageStartedAt)
    const coverageEndedAt = coverageStartedAt ? format(set(addDays(coverageStartedAtDate, warrantyTemplate.coverageDurationDays), { hours: 0, minutes: 0, seconds: 0 }), 'yyyy-MM-dd') : null
    const extendedCoverageEndedAt = coverageStartedAt ? format(set(addDays(coverageStartedAtDate, warrantyTemplate.coverageDurationDays + warrantyTemplate.extendedCoverageDurationDays), { hours: 0, minutes: 0, seconds: 0 }), 'yyyy-MM-dd') : null
    const maintenancesTypes = warrantyTemplate.maintenancesTypes?.map((type) => type.name) ?? null

    return new FormGroup({
      id: new FormControl<string>(warrantyTemplate.id!),
      name: new FormControl<string | null>(warrantyTemplate.name ?? null),
      description: new FormControl<string | null>(warrantyTemplate.description ?? null, [Validators.maxLength(2000), Validators.required, noSpacesValidator()]),
      value: new FormControl<number | null>(warrantyTemplate.value ?? null, [Validators.required, Validators.min(0), Validators.max(99999999.99)]),
      coverageDurationDays: new FormControl<number | null>(warrantyTemplate.coverageDurationDays ?? null, [Validators.required, Validators.min(1), Validators.max(2147483647)]),
      extendedCoverageDurationDays: new FormControl<number | null>(warrantyTemplate.extendedCoverageDurationDays ?? null, extendedCoverageDurationDaysValidators),
      extendedValue: new FormControl<number | null>(warrantyTemplate.extendedValue ?? null, extendedValueValidators),
      coverageStartedAt: new FormControl<string | null>(coverageStartedAt ?? null, []),
      warrantyTemplateId: new FormControl<string | null>(warrantyTemplate.id ?? null, [Validators.required]),
      hasCoverageExtend: new FormControl<boolean>(Boolean(warrantyTemplate.extendedCoverageDurationDays)),
      coverageEndedAt: new FormControl<string | null>(coverageEndedAt),
      extendedCoverageEndedAt: new FormControl<string | null>(extendedCoverageEndedAt),
      maintenancesTypes: new FormControl<string[] | null>(maintenancesTypes),
      companyId: new FormControl<string | null>(companyId  ?? null),
    })
  }

  public toCreateInput(this: void, form: FormGroup<WarrantyForm>): CreateWarrantyInput {
    return {
      description: form.get('description')?.value,
      value: form.get('value')?.value,
      coverageDurationDays: form.get('coverageDurationDays')?.value,
      extendedCoverageDurationDays: form.get('extendedCoverageDurationDays')?.value,
      extendedValue: form.get('extendedValue')?.value,
      coverageStartedAt: form.get('coverageStartedAt')?.value,
      warrantyTemplateId: form.get('warrantyTemplateId')?.value ?? '',
      companyId: form.get('companyId')?.value ?? null,
    }
  }

  public toUpdateInput(this: void, form: FormGroup<WarrantyForm>): UpdateWarrantyInput {
    const { id, name, description, value, coverageDurationDays, extendedCoverageDurationDays, extendedValue, coverageStartedAt } = form.value

    return {
      id: id ?? '',
      description: description ?? '',
      name: name ?? '',
      value: value ?? 0,
      coverageDurationDays: coverageDurationDays ?? 0,
      extendedCoverageDurationDays: extendedCoverageDurationDays ?? 0,
      extendedValue: extendedValue ?? 0,
      coverageStartedAt: coverageStartedAt ?? null,
    }
  }
}


